import React from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import DropdownLink from '../../../../common/dropdown-link';
import ArticlesItemAction from '../ArticlesItemAction';
import { useArticlePropsById } from '../../hooks/useArticlePropsById';
import { openModal } from '../../../comments-and-posts/modals/state/actions';
import {
  applyBulkAction,
  setArticlesConversationPolicy,
  setReadOnlyAction,
  updateArticleReadOnly
} from '../../reducers/loadArticlesSlice';
import { SpotPolicy } from '../../../../../../constants';
import TagBadge from '../../../comments-and-posts/dashboard/list-item/message-tags/tag';
import { extractSpotId } from '../../../../../services/extract-url';
import { useTenantConfig } from '../../../../../tenant-config/useTenantConfig';
import { BULK_ACTIONS_ENABLED, RESTRICTED_GROUPS_ENABLED } from '../../../../../tenant-config/tenantConfigConstants';
import { copyToClipboard } from '../../../../../services/copy-to-clipboard';
import { selectSpotPolicy } from '../../reducers/articlesSelectors';
import { ConfirmationModalProps } from '../../../sharable/components/modals/ConfirmationModal';
import { ModalType } from '../../../comments-and-posts/modals';
import { createTwoButtonsRenderActions } from '../../../sharable/components/modals/ConfirmationModal/confirmationModalUtils';
import { getRunningBulkActionByPostId } from '../../articlesUtils';
import { Spinner } from '../../../comments-and-posts/dashboard/components';

import styles from './ArticlesItemActions.module.scss';

import { State, useTypedSelector } from 'state';

const spotId = extractSpotId();

interface ArticleItemActionType {
  name: string;
  action: () => void;
  disabled?: boolean;
  inProgress?: boolean;
}

interface IArticlesItemActionsProps {
  articleId: string;
}

const ArticlesItemActions: React.FC<IArticlesItemActionsProps> = ({ articleId }) => {
  const currentUser = useSelector((state: State) => state.currentUser);
  const dispatch = useDispatch();
  const spotPolicy = useSelector(selectSpotPolicy);
  const { url, conversationId, policy: conversationPolicy, postId, title, pending, readOnly } =
    useArticlePropsById(articleId) ?? {};
  const isDefaultPolicy = conversationPolicy === spotPolicy;

  const runningBulkActions = useTypedSelector(state => state.moderation.articles.runningBulkActions);
  const action = getRunningBulkActionByPostId(postId, runningBulkActions);

  const isApproveAllInProgress = action === 'approve-all';
  const isArchiveAllInProgress = action === 'archive-all';
  const isRejectAllInProgress = action === 'reject-all';
  const bulkActionInProgress = isApproveAllInProgress || isArchiveAllInProgress || isRejectAllInProgress;

  const [[bulkActionsEnabled, restrictedGroupsEnabled]] = useTenantConfig([
    BULK_ACTIONS_ENABLED,
    RESTRICTED_GROUPS_ENABLED
  ]);

  const dispatchOpenModal = (modalProps: ConfirmationModalProps) => {
    dispatch(
      openModal({
        modalType: ModalType.CONFIRM_GENERAL,
        modalProps: {
          ...modalProps,
          className: styles.confirmationModal
        }
      })
    );
  };

  const bulkActionsItems: Array<ArticleItemActionType> =
    bulkActionsEnabled && pending > 0
      ? [
          !isArchiveAllInProgress &&
            !isRejectAllInProgress && {
              name: 'Approve All',
              action: () => {
                const modalProps: ConfirmationModalProps = {
                  title: 'Approve All Pending Comments',
                  description: 'This action will approve all pending comments for the selected article.',
                  renderActions: createTwoButtonsRenderActions({
                    primaryButtonLabel: 'Approve All',
                    primaryButtonOnClick: () => {
                      dispatch(applyBulkAction({ postId, title, bulkAction: 'approve-all' }));
                    }
                  })
                };

                dispatchOpenModal(modalProps);
              },
              disabled: bulkActionInProgress,
              inProgress: isApproveAllInProgress
            },
          !isApproveAllInProgress &&
            !isArchiveAllInProgress && {
              name: 'Reject All',
              action: () => {
                const modalProps: ConfirmationModalProps = {
                  title: 'Reject All Pending Comments',
                  description: `This action will reject all pending comments for the selected article.`,
                  renderActions: createTwoButtonsRenderActions({
                    primaryButtonLabel: 'Reject All',
                    primaryButtonTheme: 'contained-apply-dangerous',
                    primaryButtonOnClick: () => {
                      dispatch(applyBulkAction({ postId, title, bulkAction: 'reject-all' }));
                    }
                  })
                };

                dispatchOpenModal(modalProps);
              },
              disabled: bulkActionInProgress,
              inProgress: isRejectAllInProgress
            },
          !isApproveAllInProgress &&
            !isRejectAllInProgress && {
              name: 'Archive All',
              action: () => {
                const modalProps: ConfirmationModalProps = {
                  title: 'Archive All Pending Comments',
                  description: `This action will reject comments in the Require Approval policy as well as <br/>reported comments, and approve comments in the <br/>Publish and Moderate policy`,
                  renderActions: createTwoButtonsRenderActions({
                    primaryButtonLabel: 'Archive All',
                    primaryButtonTheme: 'contained-info',
                    primaryButtonOnClick: () => {
                      dispatch(applyBulkAction({ postId, title, bulkAction: 'archive-all' }));
                    }
                  })
                };

                dispatchOpenModal(modalProps);
              },
              disabled: bulkActionInProgress,
              inProgress: isArchiveAllInProgress
            }
        ].filter(Boolean)
      : [];

  const items: Array<ArticleItemActionType> = [
    {
      name: 'Open Article',
      action: () => {
        window.open(url, '_blank');
      }
    },
    {
      name: 'Show Conversation',
      action: () => {
        dispatch(
          openModal({
            modalType: ModalType.VIEW_CONVERSATION,
            modalProps: {
              message: { conversationId }
            }
          })
        );
      }
    },
    ...bulkActionsItems,
    {
      name: `${readOnly ? 'Open this conversation' : 'Close this conversation'} `,
      action: () => {
        dispatch(updateArticleReadOnly({ postId, readOnly: !readOnly }));
        dispatch(setReadOnlyAction({ readOnly, conversationId, postId }));
      }
    }
  ];

  if (spotPolicy !== 'require_approval') {
    const modalProps: ConfirmationModalProps = {
      title: isDefaultPolicy ? 'Enable Strict Policy?' : 'Switch to Default Policy?',
      description: isDefaultPolicy
        ? 'All comments will now require manual review unless auto-rejected. This may increase moderator load.'
        : 'Some borderline comments may become visible. Make sure this change is intentional.',
      renderActions: createTwoButtonsRenderActions({
        primaryButtonOnClick: () => {
          dispatch(
            setArticlesConversationPolicy({
              conversationId,
              policy: isDefaultPolicy ? SpotPolicy.RequireApproval : spotPolicy,
              article_strictness: isDefaultPolicy ? 'On' : 'Off',
              spotId,
              postId,
              username: currentUser.displayName,
              userID: currentUser.id
            })
          );
        },
        primaryButtonLabel: 'Confirm'
      })
    };

    items.push({
      name: `Set conversation moderation to "${isDefaultPolicy ? 'Strict' : 'Default'}"`,
      action: () => {
        dispatch(openModal({ modalType: ModalType.CONFIRM_GENERAL, modalProps }));
      }
    });
  }

  if (restrictedGroupsEnabled) {
    items.push({
      name: 'Assign/Remove Groups',
      action: () => {
        dispatch(
          openModal({
            modalType: ModalType.ASSIGN_GROUP,
            modalProps: {
              title: 'LABEL THE ARTICLE WITH RESTRICTIONS GROUPS',
              description:
                'Assign one or more restrictions groups to categorize and apply specific rules to this article based its content.',
              className: 'group-modal',
              articleId,
              postId,
              articleName: title
            }
          })
        );
      }
    });
  }

  items.push({
    name: 'Copy conversation ID to clipboard',
    action: () => {
      copyToClipboard(conversationId);
    }
  });

  const showStrictTag = conversationPolicy === SpotPolicy.RequireApproval && spotPolicy !== SpotPolicy.RequireApproval;

  return (
    <div className={cx(styles.container)}>
      {
        <div className={styles.infoTags}>
          <div className={styles.tagBadge}>
            <TagBadge
              key={'article_status'}
              color=""
              text={readOnly ? 'C' : 'O'}
              tooltip={readOnly ? 'Conversation is closed' : 'Conversation is open'}
            />
          </div>
          {showStrictTag && (
            <div className={styles.tagBadge}>
              <TagBadge key={'article_strict'} color="" text={'Strict'} tooltip="Article is set to Strict moderation" />
            </div>
          )}
          <div className={styles.line}></div>
        </div>
      }
      {bulkActionInProgress && (
        <div className={styles.bulkActionInProgress}>
          Processing <Spinner size="normal" />
          <div className={styles.line}></div>
        </div>
      )}
      <DropdownLink
        className={'item-menu'}
        items={items.map(({ name, action, disabled, inProgress }) => (
          <ArticlesItemAction name={name} onClick={action} key={name} disabled={disabled} inProgress={inProgress} />
        ))}
        text={'More'}
      />
    </div>
  );
};

export default ArticlesItemActions;
